import React, { useState, useEffect } from 'react';
import { DAILY_CAPACITY } from '../constants/daycare';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { normalizeText } from '../utils/dataUtils';
import DaycareForm from '../components/DaycareForm';
import CheckInWizard from '../components/CheckInWizard';
import CheckOutWizard from '../components/CheckOutWizard';
import DaycareVisitCard from '../components/DaycareVisitCard';
import TrialBookingForm from '../components/TrialBookingForm';
import { useDaycareVisits } from '../hooks/useDaycareVisits';
import { useTrialBookings } from '../hooks/useTrialBookings';
import { CHECKOUT_MOODS } from '../utils/daycareUtils';
import '../styles/pages/daycare-new.css';

const localIso = (date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
].join('-');

const isoToday = () => localIso(new Date());

const shiftDate = (isoDate, days) => {
    const [y, m, d] = isoDate.split('-').map(Number);
    return localIso(new Date(y, m - 1, d + days));
};

const formatDayLabel = (isoDate) => {
    const [y, m, d] = isoDate.split('-').map(Number);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const selected = new Date(y, m - 1, d);
    const diff = Math.round((today - selected) / 86400000);
    if (diff === 0) return 'Hoy';
    if (diff === 1) return 'Ayer';
    if (diff === -1) return 'Mañana';
    return selected.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
};

const DaycareManagement = () => {
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState(isoToday());
    const isToday = selectedDate === isoToday();

    const {
        visits,
        openPreviousVisits,
        loading,
        error,
        success,
        warnings,
        checkinDog,
        checkoutDog,
        clearMessages,
        clearWarnings,
    } = useDaycareVisits(selectedDate);

    const { trials, loading: trialsLoading, createTrial, updateTrial, deleteTrial } = useTrialBookings();

    const [activeTab, setActiveTab] = useState('overview');
    const [editingVisit, setEditingVisit] = useState(null);
    const [editingTrial, setEditingTrial] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [formType, setFormType] = useState('checkin'); // 'checkin', 'checkout', or 'trial'

    // Clear messages after 5 seconds
    useEffect(() => {
        if (message.text || success || error) {
            const timer = setTimeout(() => {
                setMessage({ type: '', text: '' });
                clearMessages();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message, success, error, clearMessages]);

    // Update message when hook provides success/error
    useEffect(() => {
        if (success) {
            setMessage({ type: 'success', text: success });
        } else if (error) {
            setMessage({ type: 'error', text: error });
        }
    }, [success, error]);

    // Handle form submission based on type
    const handleFormSubmit = async (formData) => {
        try {
            clearMessages();
            let operationSuccess = false;

            if (formType === 'checkin') {
                const dogIds = formData.dogIds || (formData.dogId ? [formData.dogId] : []);
                for (const dogId of dogIds) {
                    await checkinDog({ dogId, notes: formData.notes, packageId: formData.packageId, visitType: formData.visitType });
                }
                operationSuccess = true;
            } else if (formType === 'checkout') {
                // Support both legacy string notes and new mood+notes object from CheckOutWizard
                const checkoutPayload = typeof formData === 'object' && (formData.checkoutMood || formData.checkoutBehaviorNotes)
                    ? formData
                    : (typeof formData.notes === 'object' ? formData.notes : formData.notes);
                // If wizard sent { visitId, notes, checkoutMood, checkoutBehaviorNotes }, forward as object
                if (formData.checkoutMood !== undefined || formData.checkoutBehaviorNotes !== undefined) {
                    await checkoutDog(formData.visitId, { notes: formData.notes, checkoutMood: formData.checkoutMood, checkoutBehaviorNotes: formData.checkoutBehaviorNotes });
                } else {
                    await checkoutDog(formData.visitId, checkoutPayload);
                }
                operationSuccess = true;
            } else if (formType === 'trial') {
                if (editingTrial) {
                    await updateTrial(editingTrial._id, formData);
                    setMessage({ type: 'success', text: '¡Día de prueba actualizado!' });
                } else {
                    await createTrial(formData);
                    setMessage({ type: 'success', text: '¡Día de prueba agendado!' });
                }
                operationSuccess = true;
            }

            if (operationSuccess) {
                setEditingVisit(null);
                setEditingTrial(null);
                setActiveTab('overview');
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // Handle check-out action from visit card
    const handleCheckOut = (visit) => {
        setFormType('checkout');
        setActiveTab('form');
        // Pre-fill the checkout form with visit data
        setEditingVisit(visit);
    };

    // Handle edit visit
    const handleEdit = (visit) => {
        setEditingVisit(visit);
        setFormType('checkin');
        setActiveTab('form');
    };

    // Handle form cancel
    const handleFormCancel = () => {
        setEditingVisit(null);
        setEditingTrial(null);
        setFormType('checkin');
        setActiveTab('overview');
    };

    const handleEditTrial = (trial) => {
        setEditingTrial(trial);
        setFormType('trial');
        setActiveTab('form');
    };

    // Navigate to packages
    const handlePackagesNavigation = () => {
        navigate('/daycare/packages');
    };

    // Filter visits for the all visits tab
    const filteredVisits = visits.filter(visit => {
        const term = normalizeText(searchTerm);
        const matchesSearch = !term ||
            normalizeText(visit.dog?.name).includes(term) ||
            normalizeText(visit.dog?.breed).includes(term) ||
            visit.dog?.owners?.some(owner => normalizeText(owner.name).includes(term));
        
        let matchesStatus = true;
        if (statusFilter === 'active') {
            matchesStatus = visit.checkInTime && !visit.checkOutTime;
        } else if (statusFilter === 'completed') {
            matchesStatus = visit.checkInTime && visit.checkOutTime;
        } else if (statusFilter === 'pending') {
            matchesStatus = !visit.checkInTime;
        }
        
        return matchesSearch && matchesStatus;
    });

    // Derived visit lists
    const activeVisits = visits.filter(v => v.checkInTime && !v.checkOutTime);
    const completedVisits = visits.filter(v => v.checkInTime && v.checkOutTime);
    const currentlyCheckedIn = activeVisits.length;
    const completedToday = completedVisits.length;
    const totalVisits = visits.length;

    // Format a time string for display
    const fmtTime = (t) =>
        t ? new Date(t).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

    // Human-readable duration between two timestamps (defaults to now)
    const getDuration = (from, to = null) => {
        if (!from) return '';
        const mins = Math.round((new Date(to || Date.now()) - new Date(from)) / 60000);
        if (mins < 60) return `${mins}m`;
        const h = Math.floor(mins / 60), m = mins % 60;
        return m ? `${h}h ${m}m` : `${h}h`;
    };

    return (
        <div>
            <Navigation />
            <div className="daycare-container">
                <div className="daycare-header">
                    <h1>Guardería</h1>
                </div>

                {/* Messages */}
                {(message.text || success || error) && (
                    <div className={`message ${message.type || (success ? 'success' : 'error')}`}>
                        {message.text || success || error}
                    </div>
                )}

                {/* Visits never checked out. Nothing closes these on its own — the
                    day stays consumed — so they sit here until someone closes them. */}
                {openPreviousVisits.length > 0 && (
                    <div className="stale-visits">
                        <div className="stale-visits-head">
                            ⏰ {openPreviousVisits.length} visita{openPreviousVisits.length === 1 ? '' : 's'} sin checkout de días anteriores
                        </div>
                        <p className="stale-visits-note">
                            El día del paquete ya se descontó y no se devuelve. Abre el día y registra la salida.
                        </p>
                        <div className="stale-visits-list">
                            {openPreviousVisits.map(v => {
                                const dayKey = String(v.date).slice(0, 10);
                                return (
                                    <button
                                        key={v._id}
                                        type="button"
                                        className="stale-visit-row"
                                        onClick={() => {
                                            setSelectedDate(dayKey);
                                            setActiveTab('form');
                                            setFormType('checkout');
                                        }}
                                    >
                                        <span className="stale-visit-dog">{v.dog?.name || 'Perro desconocido'}</span>
                                        <span className="stale-visit-date">{formatDayLabel(dayKey)}</span>
                                        <span className="stale-visit-go">Registrar salida →</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Package warnings after check-in */}
                {warnings.length > 0 && (
                    <div className="package-warnings">
                        {warnings.map((w, i) => (
                            <div key={i} className={`package-warning package-warning--${w.type}`}>
                                <span>{w.type === 'package_complete' ? '✅' : '⚠️'} {w.message}</span>
                            </div>
                        ))}
                        <button className="package-warnings-dismiss" onClick={clearWarnings}>Cerrar</button>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="tab-navigation">
                    <button
                        onClick={() => {
                            setActiveTab('overview');
                            setEditingVisit(null);
                            setFormType('checkin');
                        }}
                        className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                    >
                        📋 Resumen
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('form');
                            setFormType('checkin');
                            setEditingVisit(null);
                        }}
                        className={`tab-button ${activeTab === 'form' && formType === 'checkin' ? 'active' : ''}`}
                    >
                        ➕ Entrada
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('form');
                            setFormType('checkout');
                            setEditingVisit(null);
                        }}
                        className={`tab-button ${activeTab === 'form' && formType === 'checkout' ? 'active' : ''}`}
                    >
                        ✅ Salida
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('all-visits');
                            setEditingVisit(null);
                        }}
                        className={`tab-button ${activeTab === 'all-visits' ? 'active' : ''}`}
                    >
                        📅 Todas las visitas
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('form');
                            setFormType('trial');
                            setEditingVisit(null);
                        }}
                        className={`tab-button ${activeTab === 'form' && formType === 'trial' ? 'active' : ''}`}
                    >
                        🎟️ Día de prueba
                    </button>
                    <button
                        onClick={handlePackagesNavigation}
                        className="tab-button packages-button"
                    >
                        📦 Paquetes
                    </button>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="dc-page">

                        {/* Day nav + summary banner combined */}
                        <div className="dc-day-banner">
                            <button className="dc-day-nav-btn" onClick={() => setSelectedDate(d => shiftDate(d, -1))}>‹</button>
                            <button
                                className={`dc-day-banner-center${isToday ? ' today' : ''}`}
                                onClick={() => setSelectedDate(isoToday())}
                                title="Volver a hoy"
                            >
                                <span className="dc-day-banner-label">{formatDayLabel(selectedDate)}</span>
                                <span className="dc-day-banner-date">
                                    {selectedDate.slice(8, 10)}/{selectedDate.slice(5, 7)}/{selectedDate.slice(0, 4)}
                                </span>
                                <span className="dc-day-banner-stats">
                                    {loading ? '…' : (
                                        <>
                                            <span className="dc-day-stat dc-day-stat--in">
                                                {currentlyCheckedIn} dentro
                                            </span>
                                            <span className="dc-day-stat-sep">·</span>
                                            <span className="dc-day-stat dc-day-stat--done">
                                                {completedToday} completadas
                                            </span>
                                            {isToday && (
                                                <>
                                                    <span className="dc-day-stat-sep">·</span>
                                                    <span className="dc-day-stat dc-day-stat--cap">
                                                        {Math.round(currentlyCheckedIn / DAILY_CAPACITY * 100)}% cap.
                                                    </span>
                                                </>
                                            )}
                                        </>
                                    )}
                                </span>
                            </button>
                            <button
                                className="dc-day-nav-btn"
                                onClick={() => setSelectedDate(d => shiftDate(d, 1))}
                                disabled={isToday}
                            >›</button>
                        </div>

                        {/* Quick actions */}
                        <div className="dc-section-row">
                            <span className="dc-section-label">{formatDayLabel(selectedDate)}</span>
                            <span className="dc-section-count">{totalVisits} visita{totalVisits !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="dc-qa-grid">
                            <button
                                className="dc-qa-card"
                                onClick={() => { setActiveTab('form'); setFormType('checkin'); setEditingVisit(null); }}
                            >
                                <div className="dc-qa-icon dc-qa-purple">📥</div>
                                <div className="dc-qa-text">
                                    <div className="dc-qa-title">Entrada</div>
                                    <div className="dc-qa-sub">Agregar un perro</div>
                                </div>
                            </button>
                            <button
                                className="dc-qa-card"
                                onClick={() => { setActiveTab('form'); setFormType('checkout'); setEditingVisit(null); }}
                            >
                                <div className="dc-qa-icon dc-qa-warm">📤</div>
                                <div className="dc-qa-text">
                                    <div className="dc-qa-title">Salida</div>
                                    <div className="dc-qa-sub">Hora de recogida</div>
                                </div>
                            </button>
                            <button
                                className="dc-qa-card dc-qa-card--trial"
                                onClick={() => { setActiveTab('form'); setFormType('trial'); setEditingVisit(null); }}
                            >
                                <div className="dc-qa-icon dc-qa-trial">🎟️</div>
                                <div className="dc-qa-text">
                                    <div className="dc-qa-title">Día de prueba</div>
                                    <div className="dc-qa-sub">Agendar visita</div>
                                </div>
                            </button>
                        </div>

                        {/* Inside now */}
                        <div className="dc-section-row">
                            <span className="dc-section-label">Dentro ahora · {currentlyCheckedIn}</span>
                        </div>
                        {loading ? (
                            <p className="dc-empty">Loading…</p>
                        ) : activeVisits.length === 0 ? (
                            <p className="dc-empty">No hay perros actualmente con entrada registrada.</p>
                        ) : (
                            <div className="dc-visit-list">
                                {activeVisits.map(visit => (
                                    <div key={visit._id} className={`dc-t-card${visit.visitType === 'trial' ? ' dc-t-card--trial' : ''}`} onClick={() => handleCheckOut(visit)}>
                                        <div className="dc-avatar">
                                            {visit.dog?.pictureUrl || visit.dog?.picture
                                                ? <img src={visit.dog.pictureUrl || visit.dog.picture} alt={visit.dog?.name} />
                                                : <span>{visit.dog?.name?.[0]?.toUpperCase() || '🐶'}</span>
                                            }
                                            <span className="dc-status-dot" />
                                        </div>
                                        <div className="dc-t-info">
                                            <div className="dc-t-name">
                                                {visit.dog?.name || 'Unknown'}
                                                {visit.visitType === 'trial' && <span className="dc-trial-badge">🎟️ Prueba</span>}
                                            </div>
                                            <div className="dc-t-meta">
                                                {visit.dog?.breed} · desde {fmtTime(visit.checkInTime)}
                                                {visit.dog?.owners?.[0] ? ` · ${visit.dog.owners[0].name}` : ''}
                                            </div>
                                        </div>
                                        <div className="dc-t-right">
                                            <span className={visit.visitType === 'trial' ? 'dc-pill dc-pill-trial' : 'dc-pill dc-pill-in'}>
                                                {visit.visitType === 'trial' ? 'Prueba' : 'Dentro'}
                                            </span>
                                            {visit.visitType !== 'trial' && (!visit.package || visit.package?.packageType === '1-day') && (
                                                <span className="dc-pill dc-pill-codo">Día Suelto</span>
                                            )}
                                            <span className="dc-t-time">{getDuration(visit.checkInTime)}</span>
                                            {(visit.dog?.transportation?.needsTransportation || visit.dog?.lunchInstructions) && (
                                                <div className="dc-extras-icons">
                                                    {visit.dog?.transportation?.needsTransportation && <span title="Transporte">🚗</span>}
                                                    {visit.dog?.lunchInstructions && <span title={visit.dog.lunchInstructions}>🍽️</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Scheduled trials */}
                        {trials.filter(t => t.status === 'scheduled').length > 0 && (
                            <>
                                <div className="dc-section-row">
                                    <span className="dc-section-label">🎟️ Días de prueba agendados · {trials.filter(t => t.status === 'scheduled').length}</span>
                                </div>
                                <div className="dc-visit-list">
                                    {trials.filter(t => t.status === 'scheduled').map(trial => {
                                        const d = new Date(trial.scheduledDate);
                                        const dateStr = d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
                                        return (
                                            <div key={trial._id} className="dc-t-card dc-t-card--trial-sched">
                                                <div className="dc-avatar dc-avatar--trial">
                                                    <span>🐶</span>
                                                </div>
                                                <div className="dc-t-info">
                                                    <div className="dc-t-name">{trial.dogName} <span className="dc-trial-badge">🎟️ Prueba</span></div>
                                                    <div className="dc-t-meta">
                                                        {trial.dogBreed && `${trial.dogBreed} · `}{trial.ownerName}{trial.phone ? ` · ${trial.phone}` : ''}
                                                    </div>
                                                </div>
                                                <div className="dc-t-right">
                                                    <span className="dc-pill dc-pill-trial">{dateStr}</span>
                                                    <div className="dc-trial-actions">
                                                        <button className="dc-trial-edit-btn" title="Editar" onClick={() => handleEditTrial(trial)}>✎</button>
                                                        <button className="dc-trial-done-btn" title="Marcar completado" onClick={() => updateTrial(trial._id, { status: 'completed' })}>✓</button>
                                                        <button className="dc-trial-cancel-btn" title="Cancelar" onClick={() => deleteTrial(trial._id)}>✕</button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* Completed today */}
                        {completedVisits.length > 0 && (
                            <>
                                <div className="dc-section-row">
                                    <span className="dc-section-label">Completadas · {completedToday}</span>
                                </div>
                                <div className="dc-visit-list">
                                    {completedVisits.map(visit => (
                                        <div key={visit._id} className="dc-t-card dc-t-card--done">
                                            <div className="dc-avatar dc-avatar--done">
                                                {visit.dog?.pictureUrl || visit.dog?.picture
                                                    ? <img src={visit.dog.pictureUrl || visit.dog.picture} alt={visit.dog?.name} />
                                                    : <span>{visit.dog?.name?.[0]?.toUpperCase() || '🐶'}</span>
                                                }
                                            </div>
                                            <div className="dc-t-info">
                                                <div className="dc-t-name">{visit.dog?.name || 'Unknown'}</div>
                                                <div className="dc-t-meta">
                                                    {visit.dog?.breed} · {fmtTime(visit.checkInTime)} → {fmtTime(visit.checkOutTime)}
                                                </div>
                                                {visit.checkoutMood && (() => {
                                                    const m = CHECKOUT_MOODS.find(x => x.value === visit.checkoutMood);
                                                    if (!m) return null;
                                                    return (
                                                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                            <span className="co-mood-badge" style={{ background: m.bg, borderColor: m.border, color: m.color, fontSize: '0.78rem', padding: '3px 8px' }}>
                                                                <span className="co-mood-emoji" style={{ fontSize: '0.95rem' }}>{m.emoji}</span> {m.label}
                                                            </span>
                                                            {visit.checkoutBehaviorNotes && (
                                                                <span style={{ fontSize: '0.82rem', color: '#4b5563', lineHeight: 1.3 }}>{visit.checkoutBehaviorNotes}</span>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                            <div className="dc-t-right">
                                                <span className="dc-pill dc-pill-done">Finalizado</span>
                                                {visit.visitType !== 'trial' && (!visit.package || visit.package?.packageType === '1-day') && (
                                                    <span className="dc-pill dc-pill-codo">Día Suelto</span>
                                                )}
                                                <span className="dc-t-time">{getDuration(visit.checkInTime, visit.checkOutTime)}</span>
                                                {(visit.dog?.transportation?.needsTransportation || visit.dog?.lunchInstructions) && (
                                                    <div className="dc-extras-icons">
                                                        {visit.dog?.transportation?.needsTransportation && <span title="Transporte">🚗</span>}
                                                        {visit.dog?.lunchInstructions && <span title={visit.dog.lunchInstructions}>🍽️</span>}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Form Tab */}
                {activeTab === 'form' && (
                    <div className="form-tab">
                        {formType === 'trial' ? (
                            <div className="dc-trial-form-wrap">
                                <h2 className="dc-trial-form-title">🎟️ Agendar día de prueba</h2>
                                <TrialBookingForm
                                    onSubmit={handleFormSubmit}
                                    onCancel={handleFormCancel}
                                    loading={trialsLoading}
                                    initialData={editingTrial}
                                />
                            </div>
                        ) : formType === 'checkin' && !editingVisit ? (
                            <CheckInWizard
                                visits={visits}
                                loading={loading}
                                onSubmit={handleFormSubmit}
                                onCancel={handleFormCancel}
                            />
                        ) : formType === 'checkout' ? (
                            <CheckOutWizard
                                visits={visits}
                                loading={loading}
                                editingVisit={editingVisit}
                                onSubmit={handleFormSubmit}
                                onCancel={handleFormCancel}
                            />
                        ) : (
                            <DaycareForm
                                onSubmit={handleFormSubmit}
                                onCancel={handleFormCancel}
                                loading={loading}
                                formType={formType}
                                editingVisit={editingVisit}
                                visits={visits}
                            />
                        )}
                    </div>
                )}

                {/* All Visits Tab */}
                {activeTab === 'all-visits' && (
                    <div className="all-visits-tab">
                        <div className="visits-header">
                            <h2>Todas las visitas de hoy</h2>

                            {/* Filters */}
                            <div className="visits-filters">
                                <div className="filter-group">
                                    <label>Buscar</label>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Buscar por nombre de perro, raza o propietario..."
                                    />
                                </div>
                                <div className="filter-group">
                                    <label>Estado</label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="">Todos los estados</option>
                                        <option value="active">Actualmente aquí</option>
                                        <option value="completed">Completada</option>
                                        <option value="pending">Pendiente</option>
                                    </select>
                                </div>
                                {(searchTerm || statusFilter) && (
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setStatusFilter('');
                                        }}
                                        className="clear-filters-btn"
                                    >
                                        Limpiar filtros
                                    </button>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading">Cargando todas las visitas...</div>
                        ) : filteredVisits.length === 0 ? (
                            <div className="no-visits">
                                {searchTerm || statusFilter ? (
                                    <p>Ninguna visita coincide con tu búsqueda.</p>
                                ) : (
                                    <p>No hay visitas registradas hoy. ¡Comienza a registrar entradas!</p>
                                )}
                            </div>
                        ) : (
                            <div className="visits-grid">
                                {filteredVisits
                                    .sort((a, b) => {
                                        const timeA = new Date(a.checkInTime || a.date);
                                        const timeB = new Date(b.checkInTime || b.date);
                                        return timeB - timeA; // Most recent first
                                    })
                                    .map(visit => (
                                        <DaycareVisitCard
                                            key={visit._id}
                                            visit={visit}
                                            onCheckOut={handleCheckOut}
                                            onEdit={handleEdit}
                                        />
                                    ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DaycareManagement;